import React, { Component } from "react";
import { TextField, Button, CircularProgress } from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import MultipleAutocomplete from "../../components/MultipleAutocomplete";
import { APIData } from "../../constants/APIAdresses";
import MediaItemCard from "./MediaItemCard";
import TagDialog from "./TagDialog";
import FileUploadDialog from "./ImageUploadDialog";
import FilePreviewDialog from "./ImagePreviewDialog";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import { ALERT_SEVERITY } from "../../components/AlertDisplay2";
import PaginationPageSelector from "./PaginationPageSelector";

export const FILE_TYPES = ["products", "categories", "backgrounds", "headers", "icons"];
export const FILE_TYPE_LABELS = {
    products: "Produktbilder",
    categories: "Kategoriebilder",
    backgrounds: "Hintergrundbilder",
    headers: "Titelbilder",
    icons: "Icons / Logos",
};

const FILE_TYPE_OPTIONS = FILE_TYPES.map(type => ({
    id: type,
    title: FILE_TYPE_LABELS[type] || type,
}));

export class MediaManagerComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            availableTypes: FILE_TYPE_OPTIONS,
            selectedTypes: props.typePreSelection
                ? FILE_TYPE_OPTIONS.filter(t => t.id === props.typePreSelection)
                : FILE_TYPE_OPTIONS.filter(t => t.id !== "icons"),
            searchText: "",
            mediaItems: [],
            tagDialogOpen: false,
            selectedImage: null,
            previewDialogOpen: false,
            uploadDialogOpen: false,
            page: 1,
            limit: 20,
            total: 0,
            deleteDialogOpen: false,
            loadingContent: false,
        };
        this.tagEditingItemIndex = null;
    }

    componentDidMount() {
        this.fetchMedia(true);
    }
    fetchMedia = async (initial, searchInactive, pageChange) => {
        if (this.state.searchText.length >= 3 || initial || searchInactive || pageChange) {
            const query = new URLSearchParams();
            query.set("search", searchInactive ? "" : this.state.searchText);
            query.set("limit", this.state.limit);
            query.set("offset", (this.state.page - 1) * this.state.limit);

            this.state.selectedTypes.forEach(t => {
                query.append("types", t.id);
            });
            this.setState({ loadingContent: true });
            const res = await fetch(`${APIData.MEDIA}/searchFiles?${query.toString()}`, {
                credentials: "include",
            });
            const json = await res.json();
            this.setState({ mediaItems: json.data || [], total: json.total || 0, loadingContent: false });
        }
    };

    onAddType = item => {
        this.setState(
            prev => ({
                selectedTypes: [...prev.selectedTypes, item],
                page: 1,
            }),
            () => this.fetchMedia(true)
        );
    };

    onDeleteType = item => {
        this.setState(
            prev => ({
                selectedTypes: prev.selectedTypes.filter(t => t.id !== item.id),
                page: 1,
            }),
            () => this.fetchMedia(true)
        );
    };

    openPreviewDialog = (item, index) => {
        this.setState({ selectedImage: item, previewDialogOpen: true });
        this.tagEditingItemIndex = index;
    };

    closePreviewDialog = () => {
        this.setState({ previewDialogOpen: false, selectedImage: null });
        this.tagEditingItemIndex = null;
    };

    handleAddTag = async () => {
        const { fileItem, onTagAdded } = this.props;
        const { tagInput } = this.state;

        if (!tagInput.trim()) return;

        this.setState({ loading: true });

        try {
            const res = await fetch(`${APIData.MEDIA}/file/${fileItem.id}/tags`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tags: tagInput.trim() }),
                credentials: 'include' // include cookies for session management
            });

            if (res.ok) {
                onTagAdded(tagInput.trim()); // let parent add it to its state
                this.setState({ tagInput: '' });
            } else {
                console.error('Failed to add tag', await res.text());
            }
        } catch (err) {
            console.error('Error adding tag', err);
        } finally {
            this.setState({ loading: false });
        }
    };

    onDeleteTag = (mainIndex, tag, idx, dialogDelete) => {
        let newMediaItems = [...this.state.mediaItems];
        let newTags = [...newMediaItems[mainIndex].tags];
        newTags.splice(idx, 1);
        newMediaItems[mainIndex].tags = newTags;

        if (!dialogDelete) {
            this.setState({ mediaItems: newMediaItems });
        } else {
            this.setState({
                selectedImage: { ...this.state.selectedImage, tags: [...newTags] },
                mediaItems: newMediaItems,
            });
        }
    };

    render() {
        return (
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                {/* Top: Search & Upload */}
                <div style={{ padding: 16, flex: "0 0 auto" }}>
                    <div style={{ display: "flex", gap: 8 }}>
                        <TextField
                            label="Suche"
                            variant="outlined"
                            size="small"
                            value={this.state.searchText}
                            fullWidth
                            onChange={e => {
                                const searchInactive =
                                    this.state.searchText.length >= 3 && e.target.value.length < 3;
                                this.setState({ searchText: e.target.value, page: 1 }, () =>
                                    this.fetchMedia(false, searchInactive)
                                );
                            }}
                        />
                        <Button
                            startIcon={<CloudUpload />}
                            variant="contained"
                            onClick={() => this.setState({ uploadDialogOpen: true })}
                        >
                            Hochladen
                        </Button>
                    </div>

                    {this.props.typePreSelection == null && (
                        <MultipleAutocomplete
                            options={this.state.availableTypes}
                            value={this.state.selectedTypes}
                            onAdd={this.onAddType}
                            onDelete={this.onDeleteType}
                            sx={{ marginTop: 1 }}
                        />
                    )}
                </div>

                {/* Scrollable Media Grid */}
                <div
                    style={{
                        flex: "1 1 auto",
                        overflowY: "auto",
                        padding: "16px",
                        overflowY: "auto",
                        scrollbarWidth: "none",         // Firefox
                        msOverflowStyle: "none",        // IE/Edge
                    }}
                >
                    {!this.state.loadingContent ?
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                                gap: "16px",
                            }}
                        >
                            {this.state.mediaItems.map((item, index) => (
                                <MediaItemCard
                                    key={item.id}
                                    item={item}
                                    index={index}
                                    onDelete={() => {
                                        this.setState({ deleteDialogOpen: true, selectedImage: item });
                                    }}
                                    onSelect={this.props.onSelect}
                                    onDeleteTag={(mainIndex, tag, idx) =>
                                        this.onDeleteTag(mainIndex, tag, idx)
                                    }
                                    openPreviewDialog={(item, index) => this.openPreviewDialog(item, index)}
                                    openTagDialog={(item, index) => {
                                        this.setState({ tagDialogOpen: true, selectedImage: item });
                                        this.tagEditingItemIndex = index;
                                    }}
                                />
                            ))}
                        </div>
                        :
                        <div style={{ position: "relative", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <CircularProgress />
                        </div>
                    }
                </div>

                {/* Pagination — fixed */}
                {/* <div
                    style={{
                        flex: "0 0 auto",
                        textAlign: "center",
                        padding: "8px",
                        borderTop: "1px solid #ddd",
                    }}
                >
                    {Array.from({ length: Math.ceil(this.state.total / this.state.limit) }, (_, i) => (
                        <Button
                            key={i}
                            variant={i + 1 === this.state.page ? "contained" : "outlined"}
                            onClick={() =>
                                this.setState({ page: i + 1 }, () => {
                                    this.fetchMedia(false, false, true);
                                })
                            }
                            sx={{ margin: "2px" }}
                        >
                            {i + 1}
                        </Button>
                    ))}
                </div> */}
                <PaginationPageSelector
                    page={this.state.page}
                    total={this.state.total}
                    limit={this.state.limit}
                    onChange={(newPage) => {
                        this.setState({ page: newPage }, () => this.fetchMedia(false, false, true));
                    }}
                />


                {/* Other dialogs */}
                <TagDialog
                    open={this.state.tagDialogOpen}
                    fileItem={this.state.selectedImage}
                    onClose={() => this.setState({ tagDialogOpen: false })}
                />
                <FileUploadDialog
                    open={this.state.uploadDialogOpen}
                    onClose={() => this.setState({ uploadDialogOpen: false })}
                    onUpload={() => {
                        this.setState({ uploadDialogOpen: false });
                        this.fetchMedia(true);
                    }}
                />
                <FilePreviewDialog
                    open={this.state.previewDialogOpen}
                    onClose={this.closePreviewDialog}
                    onAddTag={(updatedItem, mainIndex) => {
                        let newMediaItems = [...this.state.mediaItems];
                        newMediaItems[mainIndex] = updatedItem;
                        this.setState({ mediaItems: newMediaItems, selectedImage: updatedItem });
                    }}
                    fileItem={this.state.selectedImage}
                />
                <ConfirmDeleteDialog
                    open={this.state.deleteDialogOpen}
                    onClose={() => this.setState({ deleteDialogOpen: false })}
                />
            </div>
        );
    }
}
